import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconEdit } from '@components/icons/edit';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useUpdateJourneyTravellerAction } from 'api/traveller-actions/hooks';
import { TravellerJourneyActionDetailed } from 'api/types';
import { getActionStatus } from 'ui/authenticated/main/helpers';

import { ACTION_TYPES } from '../action-config';
import {
  ActionProgressBar,
  BaseContentProps,
  isActionError,
  SidebarProps,
  TravellerActionSidebar,
  useEditingContext,
} from '../common';
import { ConditionFormContent } from '../condition/helpers';
import { getConditionDescription } from '../condition/journey-node';

const ACTION_TYPE = ACTION_TYPES.break_loop;

export const SidebarContent = ({ shortId }: SidebarProps) => (
  <TravellerActionSidebar
    shortId={shortId}
    actionType={ACTION_TYPE}
    Details={DetailsContent}
    Edit={EditContent}
  />
);

type ContentProps = BaseContentProps<TravellerJourneyActionDetailed<typeof ACTION_TYPE>>;

const EditContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();

  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  return (
    <ConditionFormContent
      action={action}
      conditionDescription={t('breakLoop')}
      journeyId={action.journeyId}
      hideActionButtons
      onSubmit={(data) =>
        editMutation.mutateAsync({
          values: {
            loopAction: action.data.loopAction,
            ...data,
          },
        })
      }
      onCancel={stopEditing}
    />
  );
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const isError = isActionError(action);
  const status = getActionStatus(action);
  const { startEditing } = useEditingContext();

  const progressText =
    status === 'completed'
      ? t('completed')
      : isError
      ? t('error')
      : status === 'in_progress'
      ? t('inProgress')
      : t('notStarted');

  return (
    <Stack space={6}>
      <ActionProgressBar
        action={action}
        progressText={progressText}
        isError={isError}
        isCard={false}
        progressPercentage=" "
      />
      <Sidebar.Separator />
      <HideContainer title={t('actionConditions')}>
        <Text font="regular14" text={t('breakLoopDetailsDescription')} />
        <Text
          font="semibold14"
          text={getConditionDescription({
            values: action.values,
            inNode: false,
          })}
        />
      </HideContainer>
      <Sidebar.Separator />
      {status !== 'completed' && (
        <Sidebar.Footer>
          <IconButton variant="ghost-purple" icon={<IconEdit />} onClick={startEditing} />
        </Sidebar.Footer>
      )}
    </Stack>
  );
};

const sidebarLocale = {
  en: {
    completed: 'Completed',
    inProgress: 'In progress',
    error: 'Error',
    notStarted: 'Not started',
    dataNotFound: 'Data not found',
    actionConditions: 'Conditions',
    breakLoopDetailsDescription: 'The exit loop action is configured to exit when',
    breakLoopDescription: 'Exit the loop when specific conditions are met',
    breakLoopDescriptionType: 'Exit loop if {conditionType} of the following matches',
    breakLoop: 'Exit loop',
  },
};
